import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../api/axiosInstance';
import { toast } from 'sonner';

const t = (name, fn) =>
  createAsyncThunk(name, async (arg, { rejectWithValue }) => {
    try {
      return await fn(arg);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Request failed');
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  });

export const fetchUsers = t('user/fetchAll', async (params = {}) => {
  const { data } = await api.get('Users', { params });
  return data.data;
});

export const createUser = t('user/create', async (dto) => {
  const { data } = await api.post('Users', dto);
  toast.success('User created successfully');
  return data.data;
});

export const updateUser = t('user/update', async ({ id, dto }) => {
  const { data } = await api.put(`Users/${id}`, dto);
  toast.success('User updated');
  return data.data;
});

export const setUserActive = t('user/setActive', async ({ id, active }) => {
  await api.patch(`Users/${id}/status?active=${active}`);
  toast.success(`User ${active ? 'activated' : 'deactivated'}`);
  return { id, active };
});

export const deleteUser = t('user/delete', async (id) => {
  await api.delete(`Users/${id}`);
  toast.success('User deleted');
  return id;
});

export const assignUserRoles = t('user/assignRoles', async ({ id, roleIds }) => {
  await api.put(`Users/${id}/roles`, roleIds);
  toast.success('Roles updated');
  return { id, roleIds };
});

export const fetchRoles = t('user/fetchRoles', async () => {
  const { data } = await api.get('roles');
  return data.data;
});

export const createRole = t('user/createRole', async (roleName) => {
  const { data } = await api.post('roles', roleName);
  toast.success('Role created');
  return data.data;
});

export const deleteRole = t('user/deleteRole', async (roleId) => {
  await api.delete(`roles/${roleId}`);
  toast.success('Role deleted');
  return roleId;
});

const userSlice = createSlice({
  name: 'user',
  initialState: { items: [], roles: [], totalCount: 0, loading: false, actionLoading: false, error: null },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsers.pending, (s) => {
        s.loading = true;
        s.error = null;
      })
      .addCase(fetchUsers.fulfilled, (s, a) => {
        s.loading = false;
        s.items = a.payload?.items || [];
        s.totalCount = a.payload?.totalCount || 0;
      })
      .addCase(fetchUsers.rejected, (s, a) => {
        s.loading = false;
        s.error = a.payload;
      })
      .addCase(createUser.pending, (s) => {
        s.actionLoading = true;
      })
      .addCase(createUser.fulfilled, (s, a) => {
        s.actionLoading = false;
        s.items = [a.payload, ...s.items];
      })
      .addCase(createUser.rejected, (s) => {
        s.actionLoading = false;
      })
      .addCase(updateUser.pending, (s) => {
        s.actionLoading = true;
      })
      .addCase(updateUser.fulfilled, (s, a) => {
        s.actionLoading = false;
        s.items = s.items.map((i) => (i.userId === a.payload.userId ? a.payload : i));
      })
      .addCase(updateUser.rejected, (s) => {
        s.actionLoading = false;
      })
      .addCase(setUserActive.fulfilled, (s, a) => {
        s.items = s.items.map((i) => (i.userId === a.payload.id ? { ...i, isActive: a.payload.active } : i));
      })
      .addCase(fetchRoles.fulfilled, (s, a) => {
        s.roles = a.payload || [];
      })
      .addCase(createRole.fulfilled, (s, a) => {
        s.roles = [...s.roles, a.payload];
      })
      .addCase(deleteRole.fulfilled, (s, a) => {
        s.roles = s.roles.filter((r) => r.roleId !== a.payload);
      })
      .addCase(deleteUser.fulfilled, (s, a) => {
        s.items = s.items.filter((i) => i.userId !== a.payload);
        s.totalCount = s.totalCount - 1;
      });
  }
});

export default userSlice.reducer;
